import { useAuth } from "@/contexts/AuthContext";
import { useStore } from "@/contexts/StoreContext/StoreContext";
import {
  excludeSupportEmployees,
  sortEmployeesByName,
} from "@/services/agenda/excludeSupportEmployees";
import { Employee } from "@/services/agenda/types";
import { employeesService } from "@/services/employees";
import { error as toastError } from "@/utils/toast.utils";
import { createContext, useEffect, useMemo, useState } from "react";

interface EmployeesContextValue {
  employees: Employee[];
  loading: boolean;
}

export const EmployeesContext = createContext<EmployeesContextValue>({
  employees: [],
  loading: true,
});

const NO_EMPLOYEES: Employee[] = [];

type StoreEmployees = {
  storeExternalCode: string | null;
  employees: Employee[];
};

export const EmployeesProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  // The list carries the store it was loaded for, so after a store switch the
  // previous store's employees stop matching and the list reads as loading.
  const [loaded, setLoaded] = useState<StoreEmployees | null>(null);
  const { selectedStoreInScheduler, allStores, store } = useStore();
  const { data: authData } = useAuth();

  // Determine which store's externalCode to use for filtering
  const storeExternalCode = useMemo(() => {
    if (selectedStoreInScheduler && allStores.length > 0) {
      const selectedStore = allStores.find(
        s => s.id === selectedStoreInScheduler,
      );
      if (selectedStore) {
        return selectedStore.externalCode;
      }
    }
    // No `localStorage` fallback: that key is shared with every tab and frame on
    // this origin, and it is only written when a store resolves from a URL
    // hub_code, so it can name a different store than the one actually in use.
    return store?.externalCode || null;
  }, [selectedStoreInScheduler, allStores, store?.externalCode]);

  useEffect(() => {
    // Only the store now in use may answer: a store left earlier that answers
    // late would otherwise list its employees as this one's.
    let active = true;

    const fetchEmployees = async () => {
      // No resolved store means there is nothing to filter by. Listing every
      // employee would silently mix stores, so the list stays empty while the
      // rest of the app reports the store as unavailable.
      if (!storeExternalCode) {
        setLoaded({ storeExternalCode, employees: [] });
        return;
      }

      try {
        const { content } = await employeesService.getEmployees();
        if (!active) return;

        const activeForStore = content.filter(
          employee =>
            employee.isActive &&
            employee.stores.some(
              employeeStore => employeeStore.externalCode === storeExternalCode,
            ),
        );

        setLoaded({
          storeExternalCode,
          employees: sortEmployeesByName(
            excludeSupportEmployees(activeForStore, authData?.id),
          ),
        });
      } catch (error) {
        if (!active) return;
        console.error("Error fetching employees:", error);
        toastError(
          "Não foi possível carregar os funcionários. Atualize a página e tente novamente.",
        );
        setLoaded({ storeExternalCode, employees: [] });
      }
    };
    fetchEmployees();
    return () => {
      active = false;
    };
  }, [authData?.id, storeExternalCode]);

  const isCurrent = loaded?.storeExternalCode === storeExternalCode;
  const employees = isCurrent ? loaded.employees : NO_EMPLOYEES;
  const loading = !isCurrent;

  const value = useMemo(() => ({ employees, loading }), [employees, loading]);

  return (
    <EmployeesContext.Provider value={value}>
      {children}
    </EmployeesContext.Provider>
  );
};
